import type { HttpTransport } from '@mangovoice/api-client'
import type {
  Message,
  MessageGroup,
  MessageSnippet,
  MessageSnippetPostResponse,
  MessageThread,
  PaginatedApiResult,
} from '@mangovoice/types'

import { stringifyParams } from './utils'

export interface CreateThreadPayload {
  groupId: number
  externalPhoneNumber: string
  name?: string
}

export interface CreateThreadResponse {
  id: number
  group_id: number
  name: string
  external_phone_number: string
  created_at: string
  updated_at: string
}

export interface SendMessageResponse {
  id: number
  [key: string]: unknown
}

export interface GetMessageThreadsParams {
  offset?: number
  search?: string
  groupId?: number | string
  pbxId?: number | string
  hasUnreadMessages?: boolean
  contactId?: number
  limit?: number
}

export interface GetSnippetsParams {
  pbxId?: number
  offset?: number
  personal?: boolean
}

export function createMessagingResource(transport: HttpTransport) {
  return {
    // Message Groups
    getMessageGroups: (pbxId?: number) => {
      const qs = stringifyParams({ pbx_id: pbxId })
      return transport.get<unknown>(
        qs ? `/message-groups/?${qs}` : '/message-groups/',
      )
    },

    getMessageGroupsV1: (pbxId: number) => {
      const qs = stringifyParams({ pbx_id: pbxId })
      return transport.get<PaginatedApiResult<MessageGroup>>(
        `/app/v1/message-groups/?${qs}`,
      )
    },

    markMessageGroupAsRead: (groupId: number) =>
      transport.put<{ message: string }>(
        `/message-groups/${groupId}/mark-as-read/`,
      ),

    // Message Threads
    getThreads: (params: {
      group_id?: string | number
      external_phone_number?: string
    }) =>
      transport.get<unknown>('/message-threads/', {
        message_group_id: params.group_id,
        external_phone_number: params.external_phone_number,
      }),

    getMessageThread: (threadId: number) =>
      transport.get<MessageThread>(`/message-threads/${threadId}/`),

    getMessageThreads: (params: GetMessageThreadsParams) => {
      const qs = stringifyParams({
        ordering: '-last_message,-id',
        limit: params.limit ?? 25,
        offset: params.offset ?? 0,
        pbx_id: params.pbxId,
        group_id: params.groupId,
        message_group_id: params.groupId,
        has_unread_messages: params.hasUnreadMessages,
        search: params.search,
        contact_id: params.contactId,
      })
      return transport.get<PaginatedApiResult<MessageThread>>(
        `/message-threads/?${qs}`,
      )
    },

    createThread: (params: CreateThreadPayload) =>
      transport.post<CreateThreadResponse>('/message-threads/', {
        group_id: params.groupId,
        external_phone_number: params.externalPhoneNumber,
        name: params.name,
      }),

    updateThreadName: (threadId: number, name: string) =>
      transport.put<MessageThread>(`/message-threads/${threadId}/`, { name }),

    deleteThread: (threadId: number) =>
      transport.delete<void>(`/message-threads/${threadId}/`),

    markMessageThreadAsRead: (threadId: number) =>
      transport.put<{ message: string }>(
        `/message-threads/${threadId}/mark-as-read/`,
      ),

    // Messages
    getMessages: (params: {
      threadId: number
      offset?: number
      limit?: number
    }) => {
      const qs = stringifyParams({
        thread_id: params.threadId,
        ordering: '-id',
        offset: params.offset ?? 0,
        limit: params.limit ?? 50,
      })
      return transport.get<PaginatedApiResult<Message>>(`/messages/?${qs}`)
    },

    postMessage: (params: { threadId: number; body?: string; file?: File }) => {
      const formData = new FormData()
      formData.append('thread_id', params.threadId.toString())
      if (params.body) formData.append('body', params.body)
      if (params.file) formData.append('file', params.file)
      return transport.post<SendMessageResponse>('/messages/', formData)
    },

    // Message Snippets
    getSnippets: (params: GetSnippetsParams) => {
      const qs = stringifyParams({
        ordering: '-id',
        pbx_id: !params.personal ? params.pbxId : undefined,
        scope: params.personal ? 'user' : undefined,
        offset: params.offset,
      })
      return transport.get<PaginatedApiResult<MessageSnippet>>(
        `/message-snippets/?${qs}`,
      )
    },

    searchSnippets: (search: string) => {
      const qs = stringifyParams({
        ordering: '-id',
        limit: 500,
        search: search || undefined,
      })
      return transport.get<PaginatedApiResult<MessageSnippet>>(
        `/message-snippets/?${qs}`,
      )
    },

    postSnippet: (payload: Partial<MessageSnippet>) =>
      transport.post<MessageSnippetPostResponse>('/message-snippets/', payload),

    updateSnippet: (id: number, payload: Partial<MessageSnippet>) =>
      transport.put<MessageSnippetPostResponse>(
        `/message-snippets/${id}/`,
        payload,
      ),

    deleteSnippet: (snippetId: number) =>
      transport.delete<void>(`/message-snippets/${snippetId}/`),
  }
}
